import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
    InputAdornment,
  Autocomplete,
  Snackbar,
} from '@mui/material';
import {
  CloudUpload,
  Description,
  Download,
  Delete,
  AutoAwesome,
  Add,
  Remove,
  Edit,
  Send,
  AttachFile,
  Visibility,
  ExpandMore,
  Business,
  Person,
  Receipt,
  LocalShipping,
  Payment,
  Build,
  Warning,
  PictureAsPdf,
  ArrowBack,Search,
  Save,
} from '@mui/icons-material';
import QuotationPDFExport from './quotepdf';
import axios from 'axios';
// Modified steps - Removed the first upload step
const steps = ['Fill Quotation Details', 'Review & Generate', 'Download/Send'];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const defaultItem = {
  item_no: 1,
  description: '',
  qty: 1,
  unit: 'pc',
  unit_price: 0,
  subtotal: 0,
  cgst: 9,
  sgst: 9,
  remarks: ''
};

const defaultWarranty = {
  months: 3,
  terms: 'Standard warranty on repaired components against manufacturing defects. Does not cover physical damage or misuse.'
};

const defaultQuotationData = {
  quotation_number: '',
  date: new Date().toISOString().split('T')[0],
  valid_till: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().split('T')[0],
  company: {
    name: '',
    address: '',
    contact: '',
    gst_vat: '',
    email: '',
  },
  client: {
    name: '',
    address: '',
    contact: '',
    email: '',
    attention: '',
    gst_vat: '',
  },
  scope_of_work: '',
  items: [defaultItem],
  diagnostics_fee: 0,
  labour_cost: 0,
  parts_estimate: 0,
  testing_and_commissioning: 0,
  packaging_and_shipping: 0,
  discount_percent: 0,
  warranty: defaultWarranty,
  lead_time_days: 7,
  payment_terms: '50% advance payment upon order confirmation, 50% upon completion before delivery',
  delivery_terms: 'Ex-works (FOB Origin). Shipping costs borne by client.',
  notes: 'This quotation is valid for 30 days. Prices are subject to change without notice. Final cost may vary based on actual components required.',
  currency: 'INR',
};

const QuotationGenerator = ({ initialData, onBack }) => {
  // Add this state variable
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  // Start at step 0 (which is now "Fill Quotation Details" instead of "Upload Document or Manual Entry")
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState('company');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Initial state with all required fields
  const [quotationData, setQuotationData] = useState(defaultQuotationData);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generatedQuotation, setGeneratedQuotation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  // Load initial data or edit data on mount
  useEffect(() => {
    const loadInitialData = () => {
      // Check for edit data in sessionStorage first
      const editDataStr = sessionStorage.getItem('editQuotation');
      if (editDataStr) {
        try {
          const editData = JSON.parse(editDataStr);
          // Transform to match state structure
          const transformedData = {
            id: editData.id,
            quotation_number: editData.quotation_number || '',
            date: editData.date || new Date().toISOString().split('T')[0],
            valid_till: editData.valid_till || new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().split('T')[0],
            scope_of_work: editData.scope_of_work || '',
            items: editData.items || [defaultItem],
            diagnostics_fee: editData.diagnostics_fee || 0,
            labour_cost: editData.labour_cost || 0,
            parts_estimate: editData.parts_estimate || 0,
            testing_and_commissioning: editData.testing_and_commissioning || 0,
            packaging_and_shipping: editData.packaging_and_shipping || 0,
            discount_percent: editData.discount_percent || 0,
            warranty: {
              months: editData.warranty?.months || defaultWarranty.months,
              terms: editData.warranty?.terms || defaultWarranty.terms,
            },
            lead_time_days: editData.lead_time_days || 7,
            payment_terms: editData.payment_terms || defaultQuotationData.payment_terms,
            delivery_terms: editData.delivery_terms || defaultQuotationData.delivery_terms,
            notes: editData.notes || defaultQuotationData.notes,
            currency: editData.currency || 'INR',
            company: {
              name: editData.company_info?.name || editData.company?.name || '',
              address: editData.company_info?.address || editData.company?.address || '',
              contact: editData.company_info?.contact || editData.company?.contact || '',
              gst_vat: editData.company_info?.gst_vat || editData.company?.gst_vat || '',
              email: editData.company_info?.email || editData.company?.email || '',
            },
            client: {
              name: editData.client_info?.name || editData.client?.name || '',
              address: editData.client_info?.address || editData.client?.address || '',
              contact: editData.client_info?.contact || editData.client?.contact || '',
              email: editData.client_info?.email || editData.client?.email || '',
              attention: editData.client_info?.attention || editData.client?.attention || '',
              gst_vat: editData.client_info?.gst_vat || editData.client?.gst_vat || '',
            },
          };
          setQuotationData(transformedData);
          setIsEditMode(true);
          setEditId(editData.id);
          sessionStorage.removeItem('editQuotation');
          // Optionally jump to review step if editing
          setActiveStep(1);
        } catch (err) {
          //console.error('Failed to parse edit data:', err);
          setError('Failed to load edit data');
        }
        return;
      }

      // Fallback to props.initialData (for templates)
      if (initialData) {
        setQuotationData(initialData);
      }
    };

    loadInitialData();
        // Fetch clients and services for auto-complete
    fetchClients();
    fetchServices();
  }, [initialData]);
  // Fetch clients for auto-complete
  const fetchClients = async () => {
    setClientsLoading(true);
    try {
      const token = localStorage.getItem('token'); 
      if (!token) {
        console.warn("No authentication token found for fetching clients.");
        setClients([]);
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(`${API_URL}/api/quotation-import/clients`,  {
        headers: {
          Authorization: `Bearer ${token}`,
       
        },
        withCredentials: true,
      });
      
      if (response.data && Array.isArray(response.data)) {
        setClients(response.data);
      }
    } catch (error) {
      //console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setClientsLoading(false);
    }
  };

  // Fetch services for auto-complete
  const fetchServices = async () => {
    setServicesLoading(true);
    try {
      const token = localStorage.getItem('token'); 
      if (!token) {
        console.warn("No authentication token found for fetching services.");
        setServices([]);
        return;
      }
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.get(`${API_URL}/api/quotation-import/services`, {

        headers: {
          Authorization: `Bearer ${token}`,
      
        },
        withCredentials: true 
      });
      
      if (response.data && Array.isArray(response.data)) {
        setServices(response.data);
      }
    } catch (error) {
      //console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

    // Handle client auto-complete selection
  const handleClientSelect = (event, value) => {
    if (value) {
      setQuotationData(prev => ({
        ...prev,
        client: {
          name: value.name || '',
          address: value.address || '',
          contact: value.contact || '',
          email: value.email || '',
          attention: value.attention || '',
          gst_vat: value.gst || value.gst_vat || '',
        }
      }));
      
      setSnackbar({
        open: true,
        message: `Client "${value.name}" loaded`,
        severity: 'success'
      });
    }
  };

  // Handle service auto-complete selection for items
  const handleServiceSelect = (index, value) => {
    if (value) {
      const updatedItems = [...quotationData.items];
      updatedItems[index] = {
        ...updatedItems[index],
        description: value.description || value.name || '',
        unit: value.unit || 'pc',
        unit_price: value.unit_price || value.price || 0,
      };
      
      setQuotationData(prev => ({
        ...prev,
        items: updatedItems
      }));
      
      setSnackbar({
        open: true,
        message: `Service "${value.description || value.name}" loaded`,
        severity: 'success'
      });
    }
  };
  // Accordion handlers
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // File upload handler (optional - can be used via a button if needed)
  const handleFileUpload = async (file) => {
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('document', file);

    try {
      const response = await fetch(`${API_URL}/api/quotations/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // Merge extracted data with current state
        setQuotationData(prev => ({
          ...prev,
          ...data.data.quotation,
          // Ensure nested objects are properly merged
          company: { ...prev.company, ...(data.data.quotation.company || data.data.quotation.company_info) },
          client: { ...prev.client, ...(data.data.quotation.client || data.data.quotation.client_info) },
        }));
        setUploadedFile(file);
      } else {
        setError(data.message || 'Failed to process document');
      }
    } catch (err) {
      setError('Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  // Item management handlers
  const handleAddItem = () => {
    const newItemNo = quotationData.items.length + 1;
    setQuotationData(prev => ({
      ...prev,
      items: [...prev.items, {
        ...defaultItem,
        item_no: newItemNo,
      }]
    }));
  };

  const handleRemoveItem = (index) => {
    setQuotationData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index).map((item, idx) => ({
        ...item,
        item_no: idx + 1
      }))
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...quotationData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Calculate subtotal if quantity or unit price changes
    if (field === 'qty' || field === 'unit_price') {
      const qty = field === 'qty' ? value : updatedItems[index].qty;
      const unitPrice = field === 'unit_price' ? value : updatedItems[index].unit_price;
      updatedItems[index].subtotal = qty * unitPrice;
    }

    setQuotationData(prev => ({ ...prev, items: updatedItems }));
  };

  // Calculate totals with GST per item
  const calculateTotals = () => {
    let itemsTotal = 0;
    let cgstTotal = 0;
    let sgstTotal = 0;

    quotationData.items.forEach(item => {
      const itemSubtotal = item.qty * item.unit_price;
      itemsTotal += itemSubtotal;

      // Calculate CGST and SGST for this item
      const itemCGST = itemSubtotal * (item.cgst / 100);
      const itemSGST = itemSubtotal * (item.sgst / 100);
      cgstTotal += itemCGST;
      sgstTotal += itemSGST;
    });

    const additionalCosts = [
      'diagnostics_fee',
      'labour_cost',
      'parts_estimate',
      'testing_and_commissioning',
      'packaging_and_shipping',
    ].reduce((sum, field) => sum + (Number(quotationData[field]) || 0), 0);

    const subtotal = itemsTotal + additionalCosts;
    const discountAmount = subtotal * (quotationData.discount_percent / 100);
    const afterDiscount = subtotal - discountAmount;

    const gstTotal = cgstTotal + sgstTotal;
    const grandTotal = afterDiscount + gstTotal;

    return {
      itemsTotal,
      subtotal,
      discountAmount,
      afterDiscount,
      cgstTotal,
      sgstTotal,
      gstTotal,
      grandTotal,
      additionalCosts
    };
  };

  const totals = calculateTotals();

  // Generate quotation - Handle both create and update
  const handleGenerateQuotation = async () => {
    setLoading(true);
    setError(null);

    try {
      const { company, client, ...rest } = quotationData;
      const payload = {
        ...rest,
        company_info: company,
        client_info: client,
      };

      const method = isEditMode ? 'PUT' : 'POST';
      const url = isEditMode 
        ? `${API_URL}/api/quotations/${editId}` 
        : `${API_URL}/api/quotations/generate`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedQuotation(data.data);
        setActiveStep(2); // Move to Download/Send step (which is now step 2)
        if (isEditMode) {
          setIsEditMode(false); // Reset edit mode after successful update
          setEditId(null);
        }
      } else {
        setError(data.message || 'Failed to generate quotation');
      }
    } catch (err) {
      setError('Failed to generate quotation: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Download handlers
  const handleDownload = async (format) => {
    if (!generatedQuotation) return;

    try {
      const response = await fetch(`${API_URL}/api/quotations/${generatedQuotation.quotation.id}/download/${format}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Quotation_${generatedQuotation.quotation.quotation_number}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  // Email handler
  const handleSendEmail = async () => {
    if (!generatedQuotation) return;

    const clientEmail = generatedQuotation.quotation.client_info?.email || generatedQuotation.quotation.client?.email || '';
    const email = prompt('Enter recipient email:', clientEmail);
    if (!email) return;

    try {
      const response = await fetch(`${API_URL}/api/quotations/${generatedQuotation.quotation.id}/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      if (data.success) {
        alert('Quotation sent successfully!');
      } else {
        alert('Failed to send quotation');
      }
    } catch (err) {
      alert('Failed to send quotation');
    }
  };

  // Format currency
  const formatCurrency = (amount, curr = quotationData.currency) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: curr,
    }).format(amount || 0);
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
      {/* Stepper - Now with 3 steps instead of 4 */}
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step Content */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Step 0 (formerly Step 1) - Fill Quotation Details */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Build /> Quotation Details
                {isEditMode && <Chip label="Edit Mode" color="primary" size="small" sx={{ ml: 1 }} />}
              </Typography>

              {/* Accordion for organized sections */}
              <Accordion expanded={expandedAccordion === 'basic'} onChange={handleAccordionChange('basic')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt /> Basic Information
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Quotation Number"
                        value={quotationData.quotation_number}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, quotation_number: e.target.value }))}
                        helperText="Leave empty for auto-generation"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Currency"
                        select
                        value={quotationData.currency}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, currency: e.target.value }))}
                      >
                        <MenuItem value="INR">INR (₹)</MenuItem>
                        <MenuItem value="USD">USD ($)</MenuItem>
                        <MenuItem value="EUR">EUR (€)</MenuItem>
                        <MenuItem value="GBP">GBP (£)</MenuItem>
                        <MenuItem value="CAD">CAD (C$)</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Date"
                        type="date"
                        value={quotationData.date}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Valid Till"
                        type="date"
                        value={quotationData.valid_till}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, valid_till: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Scope of Work"
                        multiline
                        rows={3}
                        value={quotationData.scope_of_work}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, scope_of_work: e.target.value }))}
                        placeholder="Brief description of repair/retrofitting work..."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'company'} onChange={handleAccordionChange('company')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business /> Company Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={quotationData.company.name}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          company: { ...prev.company, name: e.target.value }
                        }))}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Contact Person"
                        value={quotationData.company.contact}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          company: { ...prev.company, contact: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={quotationData.company.email}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          company: { ...prev.company, email: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GST/VAT Number"
                        value={quotationData.company.gst_vat}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          company: { ...prev.company, gst_vat: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={2}
                        value={quotationData.company.address}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          company: { ...prev.company, address: e.target.value }
                        }))}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'client'} onChange={handleAccordionChange('client')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person /> Client Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Client Search Auto-complete */}
                  <Box sx={{ mb: 3 }}>
                    <Autocomplete
                      options={clients}
                      loading={clientsLoading}
                      getOptionLabel={(option) => option.name || ''}
                      onChange={handleClientSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Client"
                          placeholder="Type client name..."
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <Search />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Client Name"
                        value={quotationData.client.name}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          client: { ...prev.client, name: e.target.value }
                        }))}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Attention To"
                        value={quotationData.client.attention}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          client: { ...prev.client, attention: e.target.value }
                        }))}
                        placeholder="e.g., Accounts Department, Mr. John Doe"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Contact"
                        value={quotationData.client.contact}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          client: { ...prev.client, contact: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={quotationData.client.email}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          client: { ...prev.client, email: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="GST/VAT Number"
                        value={quotationData.client.gst_vat}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          client: { ...prev.client, gst_vat: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={2}
                        value={quotationData.client.address}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          client: { ...prev.client, address: e.target.value }
                        }))}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

 <Accordion expanded={expandedAccordion === 'items'} onChange={handleAccordionChange('items')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt /> Items & Services
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead> 
                        <TableRow sx={{ bgcolor: 'primary.light' }}>
                          <TableCell width="5%" sx={{ color: 'white' }}>#</TableCell>
                          <TableCell width="30%" sx={{ color: 'white' }}>Description</TableCell>
                          <TableCell width="10%" align="center" sx={{ color: 'white' }}>Unit</TableCell>
                          <TableCell width="15%" align="right" sx={{ color: 'white' }}>Unit Price</TableCell>
                          <TableCell width="10%" align="center" sx={{ color: 'white' }}>Qty</TableCell>
                          <TableCell width="10%" align="center" sx={{ color: 'white' }}>Subtotal</TableCell>
                          <TableCell width="7.5%" align="right" sx={{ color: 'white' }}>CGST%</TableCell>
                          <TableCell width="7.5%" align="right" sx={{ color: 'white' }}>SGST%</TableCell>
                          <TableCell width="15%" align="right" sx={{ color: 'white' }}>Amount (inc. GST)</TableCell>
                          <TableCell width="10%" align="center" sx={{ color: 'white' }}>Remarks</TableCell>
                          <TableCell width="5%" align="center" sx={{ color: 'white' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {quotationData.items.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{item.item_no}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Autocomplete
                                  freeSolo
                                  options={services}
                                  loading={servicesLoading}
                                  getOptionLabel={(option) => option.description || option.name || ''}
                                  onChange={(event, value) => handleServiceSelect(index, value)}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      size="small"
                                      value={item.description}
                                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                      placeholder="Type service description..."
                                      fullWidth
                                      sx={{width:200}}
                                    />
                                  )}
                                  sx={{ flex: 1 }}
                                />
                                <IconButton size="small" title="Search services">
                                  <Search />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Select
                                size="small"
                                value={item.unit}
                                onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                sx={{ width: 100 }}
                              >
                                <MenuItem value="pc">pc</MenuItem>
                                <MenuItem value="hr">hour</MenuItem>
                                <MenuItem value="day">day</MenuItem>
                                <MenuItem value="job">job</MenuItem>
                                <MenuItem value="set">set</MenuItem>
                                <MenuItem value="lot">lot</MenuItem>
                                <MenuItem value="kg">kg</MenuItem>
                                <MenuItem value="m">meter</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={item.unit_price}
                                onChange={(e) => handleItemChange(index, 'unit_price', Number(e.target.value))}
                                sx={{ width: 120 }}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{
                                  startAdornment: <span style={{ marginRight: 4 }}>{quotationData.currency === 'INR' ? '₹' : '$'}</span>,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                size="small"
                                value={item.qty}
                                onChange={(e) => handleItemChange(index, 'qty', Number(e.target.value))}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0, step: 0.5 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(item.subtotal)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={item.cgst}
                                onChange={(e) => handleItemChange(index, 'cgst', Number(e.target.value))}
                                sx={{ width: 70 }}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={item.sgst}
                                onChange={(e) => handleItemChange(index, 'sgst', Number(e.target.value))}
                                sx={{ width: 70 }}
                                inputProps={{ min: 0, max: 100, step: 0.1 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="bold">
                                {formatCurrency(
                                  item.subtotal +
                                  (item.subtotal * item.cgst / 100) +
                                  (item.subtotal * item.sgst / 100)
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                value={item.remarks}
                                onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                                placeholder="Optional"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(index)}
                                color="error"
                                disabled={quotationData.items.length === 1}
                              >
                                <Remove />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Button
                    startIcon={<Add />}
                    onClick={handleAddItem}
                    sx={{ mt: 2 }}
                    variant="outlined"
                  >
                    Add Item
                  </Button>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'additional'} onChange={handleAccordionChange('additional')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build /> Additional Costs
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Diagnostics/Evaluation Fee"
                        type="number"
                        value={quotationData.diagnostics_fee}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, diagnostics_fee: Number(e.target.value) || 0 }))}
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 4 }}>{quotationData.currency === 'INR' ? '₹' : '$'}</span>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Labour & Repair Cost"
                        type="number"
                        value={quotationData.labour_cost}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, labour_cost: Number(e.target.value) || 0 }))}
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 4 }}>{quotationData.currency === 'INR' ? '₹' : '$'}</span>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Parts Estimate"
                        type="number"
                        value={quotationData.parts_estimate}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, parts_estimate: Number(e.target.value) || 0 }))}
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 4 }}>{quotationData.currency === 'INR' ? '₹' : '$'}</span>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Testing & Commissioning"
                        type="number"
                        value={quotationData.testing_and_commissioning}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, testing_and_commissioning: Number(e.target.value) || 0 }))}
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 4 }}>{quotationData.currency === 'INR' ? '₹' : '$'}</span>,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Packaging & Shipping"
                        type="number"
                        value={quotationData.packaging_and_shipping}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, packaging_and_shipping: Number(e.target.value) || 0 }))}
                        InputProps={{
                          startAdornment: <span style={{ marginRight: 4 }}>{quotationData.currency === 'INR' ? '₹' : '$'}</span>,
                        }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'discount'} onChange={handleAccordionChange('discount')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Receipt /> Discount
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Discount Percentage (%)"
                        type="number"
                        value={quotationData.discount_percent}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, discount_percent: Number(e.target.value) || 0 }))}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        helperText={`Discount amount: ${formatCurrency(totals.discountAmount)}`}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion expanded={expandedAccordion === 'terms'} onChange={handleAccordionChange('terms')}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping /> Terms & Conditions
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Warranty (Months)"
                        type="number"
                        value={quotationData.warranty.months}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          warranty: { ...prev.warranty, months: Number(e.target.value) || 0 }
                        }))}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Lead Time (Days)"
                        type="number"
                        value={quotationData.lead_time_days}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, lead_time_days: Number(e.target.value) || 0 }))}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Warranty Terms"
                        multiline
                        rows={2}
                        value={quotationData.warranty.terms}
                        onChange={(e) => setQuotationData(prev => ({
                          ...prev,
                          warranty: { ...prev.warranty, terms: e.target.value }
                        }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Payment Terms"
                        multiline
                        rows={2}
                        value={quotationData.payment_terms}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, payment_terms: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Delivery Terms"
                        multiline
                        rows={2}
                        value={quotationData.delivery_terms}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, delivery_terms: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Additional Notes"
                        multiline
                        rows={3}
                        value={quotationData.notes}
                        onChange={(e) => setQuotationData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Any additional information, conditions, or special instructions..."
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              {/* Summary Card */}
              <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50', border: 1, borderColor: 'primary.light' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt /> Quotation Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Items Total:</Typography>
                        <Typography fontWeight="bold">{formatCurrency(totals.itemsTotal)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Additional Costs:</Typography>
                        <Typography>{formatCurrency(totals.additionalCosts)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Subtotal:</Typography>
                        <Typography fontWeight="bold">{formatCurrency(totals.subtotal)}</Typography>
                      </Box>
                      {quotationData.discount_percent > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                          <Typography>Discount ({quotationData.discount_percent}%):</Typography>
                          <Typography>-{formatCurrency(totals.discountAmount)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>After Discount:</Typography>
                        <Typography>{formatCurrency(totals.afterDiscount)}</Typography>
                      </Box>

                      {/* GST Display */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>CGST on Items:</Typography>
                        <Typography>{formatCurrency(totals.cgstTotal)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>SGST on Items:</Typography>
                        <Typography>{formatCurrency(totals.sgstTotal)}</Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Total GST:</Typography>
                        <Typography variant="h6">{formatCurrency(totals.gstTotal)}</Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="h5" color="primary">GRAND TOTAL:</Typography>
                        <Typography variant="h5" color="primary">{formatCurrency(totals.grandTotal)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        <Warning fontSize="small" sx={{ mr: 1 }} /> Important Notes
                      </Typography>
                      <Typography variant="body2">
                        • Quotation valid until: {new Date(quotationData.valid_till).toLocaleDateString()}<br />
                        • Warranty: {quotationData.warranty.months} months<br />
                        • Lead time: {quotationData.lead_time_days} days<br />
                        • Payment terms: {quotationData.payment_terms.substring(0, 50)}...
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Step 1 (formerly Step 2) - Review & Generate */}
          {activeStep === 1 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {loading ? (
                <>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    AI is Generating Professional Quotation...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Creating a detailed proforma quotation with calculations and professional formatting
                  </Typography>
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip label="Processing Data" color="primary" variant="outlined" />
                    <Chip label="Calculating Totals" color="primary" variant="outlined" />
                    <Chip label="Formatting Document" color="primary" variant="outlined" />
                    <Chip label="Finalizing" color="primary" variant="outlined" />
                  </Box>
                </>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Ready to {isEditMode ? 'Update' : 'Generate'} Quotation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Review the details below and {isEditMode ? 'update' : 'generate'} your professional quotation
                  </Typography>

                  <Paper sx={{ p: 3, mb: 3, textAlign: 'left', bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">Preview:</Typography>
                    <Typography variant="body2">
                      <strong>Quotation:</strong> {quotationData.quotation_number || '(Auto-generate)'}<br />
                      <strong>Client:</strong> {quotationData.client?.name || ''}<br />
                      <strong>Total Amount:</strong> {formatCurrency(totals.grandTotal)}<br />
                      <strong>Items:</strong> {quotationData.items.length} item(s)<br />
                      <strong>Valid Until:</strong> {new Date(quotationData.valid_till).toLocaleDateString()}
                    </Typography>
                  </Paper>

                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleGenerateQuotation}
                      startIcon={<AutoAwesome />}
                      size="large"
                    >
                      {isEditMode ? 'Update' : 'Generate'} Quotation
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setPreviewOpen(true)}
                      startIcon={<Visibility />}
                      size="large"
                    >
                      Preview Details
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Step 2 (formerly Step 3) - Download/Send */}
          {activeStep === 2 && generatedQuotation && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoAwesome color="primary" /> Quotation {isEditMode ? 'Updated!' : 'Ready!'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/* <Button
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => setPreviewOpen(true)}
                  >
                    Preview
                  </Button> */}
                  {/* <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleSendEmail}
                  >
                    Send Email
                  </Button> */}
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, height: '100%', border: 1, borderColor: 'success.light' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description color="primary" /> Generated Document
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                      <Chip
                        label={`Quotation #${generatedQuotation.quotation.quotation_number}`}
                        color="success"
                        variant="filled"
                        sx={{ fontSize: '1rem', padding: 1.5 }}
                      />
                      <Chip
                        label={`Status: ${generatedQuotation.quotation.status}`}
                        color={generatedQuotation.quotation.status === 'draft' ? 'warning' : 'info'}
                        variant="outlined"
                        sx={{ fontSize: '0.9rem' }}
                      />
                      <Chip
                        label={`Amount: ${formatCurrency(generatedQuotation.quotation.grand_total, generatedQuotation.quotation.currency)}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.9rem' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      A professional quotation has been {isEditMode ? 'updated and' : ''} saved to your account.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, height: '100%', border: 1, borderColor: 'primary.light' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Download color="primary" /> Download Options
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<PictureAsPdf />}
                        onClick={() => setPdfExportOpen(true)}
                        size="large"
                      >
                        Export PDF (Preview)
                      </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
                      Download in your preferred format for sharing or record-keeping
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, height: '100%', border: 1, borderColor: 'secondary.light' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Send color="primary" /> Share & Actions
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<Send />}
                        onClick={handleSendEmail}
                        size="large"
                      >
                        Send to Client
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<Delete />}
                        color="error"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to discard this quotation?')) {
                            setActiveStep(0);
                            setGeneratedQuotation(null);
                            setUploadedFile(null);
                          }
                        }}
                        size="large"
                      >
                        Discard
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={loading}
          onClick={() => {
            if (activeStep === 0) {
              onBack && onBack();
            } else {
              setActiveStep(prev => prev - 1);
            }
          }}
          variant="outlined"
          startIcon={<ArrowBack />}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setActiveStep(0);
              setQuotationData(defaultQuotationData);
              setGeneratedQuotation(null);
              setUploadedFile(null);
              setIsEditMode(false);
              setEditId(null);
            }}
            startIcon={<Delete />}
            disabled={loading}
            color="error"
          >
            Reset All
          </Button>

          {activeStep === 0 && (
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!(quotationData.client?.name && quotationData.company?.name)}
            >
              Continue to Review
            </Button>
          )}

          {activeStep === 1 && !loading && (
            <Button
              variant="contained"
              onClick={handleGenerateQuotation}
              startIcon={<AutoAwesome />}
            >
              {isEditMode ? 'Update' : 'Generate'} Quotation
            </Button>
          )}
        </Box>
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility /> Quotation Preview
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {generatedQuotation?.proforma ? (
            <Paper sx={{ p: 3, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem' }}>
              {generatedQuotation.proforma}
            </Paper>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>Quotation Summary</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Basic Information</Typography>
                  <Typography><strong>Number:</strong> {quotationData.quotation_number || '(Auto-generate)'}</Typography>
                  <Typography><strong>Date:</strong> {quotationData.date}</Typography>
                  <Typography><strong>Valid Till:</strong> {quotationData.valid_till}</Typography>
                  <Typography><strong>Currency:</strong> {quotationData.currency}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Parties</Typography>
                  <Typography><strong>Company:</strong> {quotationData.company?.name || ''}</Typography>
                  <Typography><strong>Client:</strong> {quotationData.client?.name || ''}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Scope of Work</Typography>
                  <Typography>{quotationData.scope_of_work}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Financial Summary</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography><strong>Items Total:</strong> {formatCurrency(totals.itemsTotal)}</Typography>
                    <Typography><strong>Additional Costs:</strong> {formatCurrency(totals.additionalCosts)}</Typography>
                    <Typography><strong>Subtotal:</strong> {formatCurrency(totals.subtotal)}</Typography>
                    {quotationData.discount_percent > 0 && (
                      <Typography><strong>Discount:</strong> -{formatCurrency(totals.discountAmount)}</Typography>
                    )}
                    <Typography><strong>Taxes:</strong> {formatCurrency(totals.gstTotal)}</Typography>
                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                      <strong>Grand Total:</strong> {formatCurrency(totals.grandTotal)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Terms & Conditions</Typography>
                  <Typography><strong>Warranty:</strong> {quotationData.warranty.months} months</Typography>
                  <Typography><strong>Lead Time:</strong> {quotationData.lead_time_days} days</Typography>
                  <Typography><strong>Payment:</strong> {quotationData.payment_terms}</Typography>
                  <Typography><strong>Delivery:</strong> {quotationData.delivery_terms}</Typography>
                  {quotationData.notes && (
                    <Typography sx={{ mt: 1 }}><strong>Notes:</strong> {quotationData.notes}</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {!generatedQuotation && (
            <Button
              variant="contained"
              onClick={() => {
                setPreviewOpen(false);
                handleGenerateQuotation();
              }}
            >
              {isEditMode ? 'Update' : 'Generate'} Now
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <QuotationPDFExport
        open={pdfExportOpen}
        onClose={() => setPdfExportOpen(false)}
        quotationData={generatedQuotation?.quotation || quotationData}
      />
    </Box>
  );
};

export default QuotationGenerator;